// Asistente IA del Portal Tickets (el "Analista"): botón flotante presente en todos los menús del
// portal que abre el chat global del admin (`ChatIaPanel`: el mismo panel, historial y UX que la
// Guía del portal y el Agente CEO). Llama a la edge function analista-tickets, que orquesta a
// Claude con tool-use sobre las MISMAS RPCs del tablero (nunca SQL libre), así que el chat y el
// dashboard dan el mismo número. Usa los filtros guardados del tablero (ámbito y periodo,
// `filtrosAnalitica`), así responde igual desde cualquier menú.
//
// Lo propio del analista: la respuesta se pinta con formato (conclusión destacada, viñetas,
// números resaltados y las acciones numeradas como tarjetas por urgencia), el pie dice con qué
// herramientas se calculó y "Copiar plan" copia sin formato.
//
// Historial: la función guarda en ia_conversaciones/ia_mensajes del agente (`bot-agente-tickets`)
// y responde `id_conversacion`. Si la versión desplegada aún no lo hace, se sigue mandando el
// `historial` del hilo como antes y el historial del panel avisa que llegará al actualizarla.
//
// Es un agente administrable (`ia_agentes`, tipo analista, Sistema → Asistentes IA): nombre,
// descripción y sugerencias llegan en `agente` desde `ia_agentes_disponibles()`; si vienen
// vacíos se usan los textos de siempre.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Info, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { BotonFlotanteIA } from "@/components/admin/BotonFlotanteIA";
import { ChatIaCuerpo, ChatIaPanel } from "@/components/admin/ChatIaPanel";
import { ChatIaHistorial } from "@/components/admin/ChatIaHistorial";
import { AccionesMensaje, BotonCopiar, ChatIaAviso, ChatIaMensajes, EsqueletoHilo } from "@/components/admin/ChatIaMensajes";
import { ChatIaComposer } from "@/components/admin/ChatIaComposer";
import { ChatIaBienvenida } from "@/components/admin/ChatIaBienvenida";
import { useHistorialChatIa } from "@/hooks/useHistorialChatIa";
import { useChatIaPanel } from "@/hooks/useChatIaPanel";
import { filtrosAnalitica } from "@/lib/portal-tickets/filtros";
import {
  ANALISTA_FUNCION,
  cuerpoAnalista,
  leerRespuestaAnalista,
  textoParaCopiarAnalista,
} from "@/lib/portal-tickets/analista";
import { primeraPregunta, type ConversacionIa, type MensajeIa } from "@/lib/chatIa";
import { mostrarRestantes, textoRestantes, type AgenteDisponible } from "@/lib/guiaPortal";
import { rangoAnalitica, type Filtros } from "./hooks";

/** Respaldo si el agente no trae sugerencias (columna sin migrar o lista vaciada en el panel). */
const SUGERIDAS = [
  "¿Cómo vamos este mes?",
  "¿Qué se repite más y es realmente una queja?",
  "¿Dónde está el cuello de botella?",
  "¿Qué 3 cosas debería atender esta semana?",
];
const DESCRIPCION_RESPALDO =
  "Responde con los mismos números del tablero, con el ámbito y el periodo que tienes filtrados.";

/** Tope de la pregunta: el mismo de la guía. */
const MAX_PREGUNTA = 500;

// Resalta números clave (%, tiempos, conteos) para que salten a la vista.
const NUM = /\d[\d.,]*\s?%|\d+\s?(?:días|día|horas|hora|hrs|hr|tickets|ticket)\b|\b\d{2,3}\b/i;
function withNumbers(text: string, k: string): React.ReactNode {
  return text.split(new RegExp(`(${NUM.source})`, "gi")).map((p, i) =>
    new RegExp(`^(?:${NUM.source})$`, "i").test(p)
      ? <span key={`${k}n${i}`} className="font-semibold tabular-nums text-foreground">{p}</span>
      : <span key={`${k}t${i}`}>{p}</span>);
}
// Negritas **así** + números resaltados.
function inline(text: string, k: string): React.ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`${k}-${i}`} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>
      : <span key={`${k}-${i}`}>{withNumbers(p, `${k}-${i}`)}</span>);
}

// Urgencia según el "Plazo": hoy/urgente → alta (ámbar), esta semana/días →
// media (marca), más lejano → baja (neutro).
function urgencia(subs: string[]) {
  const plazo = (subs.find((s) => /^\*{0,2}\s*plazo/i.test(s)) ?? subs.join(" ")).toLowerCase();
  if (/\bhoy\b|\bya\b|inmediat|urgente|ahora|\bmañana\b/.test(plazo))
    return { bar: "border-l-amber-500", badge: "bg-amber-500 text-white", chip: { label: "Urgente", cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/15" } };
  if (/semana|d[ií]a|h[aá]bil/.test(plazo))
    return { bar: "border-l-primary", badge: "bg-primary text-primary-foreground", chip: { label: "Esta semana", cls: "bg-primary/10 text-primary" } };
  return { bar: "border-l-slate-300 dark:border-l-slate-600", badge: "bg-muted text-muted-foreground", chip: null as null | { label: string; cls: string } };
}

// Tarjeta de acción: color por urgencia, número en badge, título y subcampos.
function ActionCard({ num, title, subs }: { num: string; title: string; subs: string[] }) {
  const u = urgencia(subs);
  return (
    <div className={cn("rounded-xl border border-border border-l-[3px] bg-card px-3.5 py-3 my-2 shadow-sm", u.bar)}>
      <div className="flex items-start gap-2.5">
        <span className={cn("inline-flex items-center justify-center w-6 h-6 rounded-lg text-[12px] font-bold shrink-0 tabular-nums", u.badge)}>{num}</span>
        <p className="flex-1 text-[13.5px] font-semibold text-foreground leading-snug pt-0.5">{inline(title, `t${num}`)}</p>
        {u.chip && <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold", u.chip.cls)}>{u.chip.label}</span>}
      </div>
      {subs.length > 0 && (
        <div className="mt-2.5 pl-[34px] space-y-1.5">
          {subs.map((s, j) => {
            const mm = s.match(/^\*{0,2}\s*([A-Za-zÁÉÍÓÚáéíóúÑñ][A-Za-zÁÉÍÓÚáéíóúÑñ ]{1,18}?)\s*:\*{0,2}\s*(.*)$/);
            return mm ? (
              <div key={j} className="flex gap-2 text-[12.5px]">
                <span className="text-muted-foreground shrink-0 min-w-[58px]">{mm[1]}</span>
                <span className="flex-1 text-foreground">{inline(mm[2], `s${num}-${j}`)}</span>
              </div>
            ) : (
              <div key={j} className="text-[12.5px] text-foreground">{inline(s, `s${num}-${j}`)}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Render del markdown del analista: conclusión destacada, subtítulos, viñetas y
// las acciones numeradas como tarjetas. Sin librería nueva.
function Respuesta({ texto }: { texto: string }) {
  const lines = texto.replace(/\r/g, "").split("\n");
  const out: React.ReactNode[] = [];
  let i = 0; let primer = true;
  while (i < lines.length) {
    const raw = lines[i];
    if (raw.trim() === "") { out.push(<div key={`s${i}`} className="h-2" />); i++; continue; }
    const topOrdered = raw.match(/^(\d+)\.\s+(.*)$/);
    if (topOrdered) {
      const subs: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== "" && /^\s+\S/.test(lines[i])) {
        subs.push(lines[i].replace(/^\s*[-*]\s*/, "").trim());
        i++;
      }
      out.push(<ActionCard key={`a${i}`} num={topOrdered[1]} title={topOrdered[2]} subs={subs} />);
      primer = false;
      continue;
    }
    const bullet = raw.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      const indent = raw.match(/^\s*/)?.[0].length ?? 0;
      out.push(
        <div key={`b${i}`} className={cn("flex gap-2", indent >= 2 ? "pl-5" : "")}>
          <span className="text-primary/70 mt-[2px] leading-none">•</span>
          <span className="flex-1">{inline(bullet[1], `b${i}`)}</span>
        </div>);
      primer = false; i++; continue;
    }
    const t = raw.trim();
    const heading = /^\*\*.+\*\*:?$/.test(t);
    if (heading) out.push(<p key={`h${i}`} className="font-semibold text-foreground mt-2.5 mb-0.5">{inline(t, `h${i}`)}</p>);
    else if (primer) out.push(<p key={`p${i}`} className="text-[14.5px] font-medium text-foreground leading-snug mb-1">{inline(t, `p${i}`)}</p>);
    else out.push(<p key={`p${i}`} className="leading-relaxed">{inline(t, `p${i}`)}</p>);
    primer = false; i++;
  }
  return <div className="space-y-1 text-[13.5px] text-foreground">{out}</div>;
}

let contadorIds = 0;
const nuevoId = (prefijo: string) => `${prefijo}-${Date.now().toString(36)}-${++contadorIds}`;

/**
 * Conversación con el analista. Vive en el componente (cerrar y abrir el panel no la pierde).
 * `persiste`: `null` sin saber todavía, `false` si la función respondió sin `id_conversacion`.
 */
function useChatAnalista(filtros: Filtros, alGuardar: () => void) {
  const [mensajes, setMensajes] = useState<MensajeIa[]>([]);
  const [idConversacion, setIdConversacion] = useState<number | null>(null);
  const [persiste, setPersiste] = useState<boolean | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<{ codigo: string; mensaje: string } | null>(null);
  const [restantesHora, setRestantesHora] = useState<number | null>(null);
  const generacion = useRef(0);
  const ultimo = useRef<string | null>(null);
  const estado = useRef({ mensajes, idConversacion });
  estado.current = { mensajes, idConversacion };

  const pedir = useCallback(
    async (texto: string, { nueva = false, agregar = true }: { nueva?: boolean; agregar?: boolean } = {}) => {
      const gen = ++generacion.current;
      const vigente = () => generacion.current === gen;
      const id = nueva ? null : estado.current.idConversacion;
      // Sin número, la función necesita el hilo anterior (sin la pregunta que va en `pregunta`).
      const previo = nueva ? [] : agregar ? estado.current.mensajes : estado.current.mensajes.slice(0, -1);
      ultimo.current = texto;
      setError(null);
      setEnviando(true);
      if (nueva) setIdConversacion(null);
      if (agregar) {
        const pregunta: MensajeIa = { id: nuevoId("u"), rol: "usuario", texto, herramientas: [] };
        setMensajes(nueva ? [pregunta] : (m) => [...m, pregunta]);
      }
      try {
        const { data, error: errInvoke } = await supabase.functions.invoke(ANALISTA_FUNCION, {
          body: cuerpoAnalista({ pregunta: texto, idConversacion: id, hilo: previo, filtros }),
        });
        if (!vigente()) return;
        if (errInvoke) throw errInvoke;
        const r = leerRespuestaAnalista(data);
        // `in` y no `!r.ok`: sin strictNullChecks el literal booleano no estrecha la unión.
        if ("codigo" in r) {
          setError({ codigo: r.codigo, mensaje: r.mensaje });
          return;
        }
        ultimo.current = null;
        setMensajes((m) => [...m, { id: nuevoId("a"), rol: "asistente", texto: r.texto, herramientas: r.herramientas }]);
        if (r.restantesHora !== null) setRestantesHora(r.restantesHora);
        if (r.idConversacion !== null) {
          setIdConversacion(r.idConversacion);
          setPersiste(true);
          alGuardar();
        } else {
          setPersiste(false);
        }
      } catch (e: any) {
        if (vigente()) setError({ codigo: "error_red", mensaje: `Ocurrió un error: ${e?.message ?? e}` });
      } finally {
        if (vigente()) setEnviando(false);
      }
    },
    [filtros, alGuardar],
  );

  const reiniciar = useCallback(() => {
    generacion.current++;
    ultimo.current = null;
    setEnviando(false);
    setError(null);
  }, []);

  const nueva = useCallback(() => {
    reiniciar();
    setMensajes([]);
    setIdConversacion(null);
  }, [reiniciar]);

  const abrir = useCallback(
    (numero: number, hilo: MensajeIa[]) => {
      reiniciar();
      setMensajes(hilo);
      setIdConversacion(numero);
    },
    [reiniciar],
  );

  const reintentar = useCallback(() => {
    if (ultimo.current && !enviando) void pedir(ultimo.current, { agregar: false });
  }, [enviando, pedir]);

  return { mensajes, idConversacion, persiste, enviando, error, restantesHora, pedir, nueva, abrir, reintentar };
}

export function AnalistaFlotante({ agente }: { agente: AgenteDisponible }) {
  const nombre = agente.nombre.trim() || "Agente Tickets";
  const descripcion = agente.descripcion.trim() || DESCRIPCION_RESPALDO;
  const sugeridas = agente.sugerencias.length > 0 ? agente.sugerencias : SUGERIDAS;
  const [scope] = filtrosAnalitica.useFiltro("ambito");
  const [periodo] = filtrosAnalitica.useFiltro("periodo");
  const [rango] = filtrosAnalitica.useFiltro("rango");
  const filtros: Filtros = useMemo(() => ({ ...rangoAnalitica(periodo, rango), scope }), [periodo, rango, scope]);
  const { toast } = useToast();
  const panel = useChatIaPanel();
  const historial = useHistorialChatIa(agente.codigo, { habilitado: panel.abierto });
  const chat = useChatAnalista(filtros, historial.refrescar);
  const [borrador, setBorrador] = useState("");
  const [abriendo, setAbriendo] = useState<string | null>(null);
  const finRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const vacio = chat.mensajes.length === 0;
  const bloqueado = chat.error?.codigo === "sin_permiso" || chat.error?.codigo === "deshabilitado";

  useEffect(() => {
    if (panel.abierto) finRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [panel.abierto, chat.mensajes.length, chat.enviando, chat.error]);

  const enfocarInput = () => window.setTimeout(() => inputRef.current?.focus(), 0);
  const activa = historial.conversaciones.find((c) => c.numero !== null && c.numero === chat.idConversacion)?.id ?? null;

  const enviar = (texto: string, opciones?: { nueva?: boolean }) => {
    const q = texto.trim().slice(0, MAX_PREGUNTA);
    if (!q || chat.enviando) return;
    if (texto === borrador) setBorrador("");
    void chat.pedir(q, opciones);
  };

  const repetir = (texto: string) => {
    panel.volverAlChat();
    enviar(texto, { nueva: true });
  };

  const nuevaConversacion = () => {
    chat.nueva();
    setBorrador("");
    panel.volverAlChat();
    enfocarInput();
  };

  const cargar = async (c: ConversacionIa) => {
    setAbriendo(c.id);
    try {
      return await historial.cargarMensajes(c.id);
    } catch {
      toast({ title: "No se pudo abrir la conversación", description: "Intenta de nuevo en un momento.", variant: "destructive" });
      return null;
    } finally {
      setAbriendo(null);
    }
  };

  const abrirConversacion = async (c: ConversacionIa) => {
    if (c.numero === null) return;
    if (c.numero === chat.idConversacion) {
      panel.volverAlChat();
      return;
    }
    const hilo = await cargar(c);
    if (!hilo) return;
    chat.abrir(c.numero, hilo);
    setBorrador("");
    panel.volverAlChat();
  };

  const repetirConversacion = async (c: ConversacionIa) => {
    const hilo = await cargar(c);
    if (!hilo) return;
    const pregunta = primeraPregunta(hilo);
    if (!pregunta) {
      toast({ title: "Esa conversación no tiene preguntas para repetir" });
      return;
    }
    repetir(pregunta);
  };

  const eliminarConversacion = (c: ConversacionIa) => {
    historial.actualizar.mutate(
      { id: c.id, eliminar: true },
      { onError: () => toast({ title: "No se pudo eliminar la conversación", variant: "destructive" }) },
    );
    if (c.numero !== null && c.numero === chat.idConversacion) chat.nueva();
  };

  const editar = (texto: string) => {
    setBorrador(texto.slice(0, MAX_PREGUNTA));
    enfocarInput();
  };

  return (
    <>
      <BotonFlotanteIA
        icono={Sparkles}
        texto={nombre}
        tooltip="Pregúntale por los números del portal"
        ariaLabel={`Abrir ${nombre}`}
        onClick={() => panel.setAbierto(true)}
        abierto={panel.abierto}
        orden={1}
      />

      <ChatIaPanel
        abierto={panel.abierto}
        onAbiertoChange={panel.setAbierto}
        titulo={nombre}
        descripcion={descripcion}
        historialAbierto={panel.historialAbierto}
        onAlternarHistorial={panel.alternarHistorial}
        onNueva={nuevaConversacion}
        nuevaDeshabilitada={vacio && !chat.enviando}
        historial={
          <ChatIaHistorial
            conversaciones={historial.conversaciones}
            cargando={historial.cargando}
            error={historial.error}
            activa={activa}
            abriendo={abriendo}
            puedePreguntar={!chat.enviando && !bloqueado}
            onAbrir={(c) => void abrirConversacion(c)}
            onRepetir={(c) => void repetirConversacion(c)}
            onRenombrar={(c, titulo) => historial.actualizar.mutate({ id: c.id, titulo })}
            onFijar={(c, fijada) => historial.actualizar.mutate({ id: c.id, fijada })}
            onEliminar={eliminarConversacion}
            onReintentar={() => void historial.recargar()}
            aviso={
              chat.persiste === false ? (
                <>
                  <p className="text-[12.5px] font-medium text-foreground">Disponible al actualizar el asistente</p>
                  <p className="mt-1 text-[11.5px] leading-snug text-muted-foreground">
                    Por ahora la conversación vive solo en este panel mientras no recargues la página.
                  </p>
                </>
              ) : undefined
            }
          />
        }
      >
        <ChatIaCuerpo
          composer={
            <ChatIaComposer
              inputRef={inputRef}
              valor={borrador}
              onCambiar={setBorrador}
              onEnviar={() => enviar(borrador)}
              bloqueado={bloqueado}
              enviando={chat.enviando}
              maxLength={MAX_PREGUNTA}
              placeholder="Escribe tu pregunta"
              ariaLabel="Tu pregunta para el asistente"
              pie={mostrarRestantes(chat.restantesHora) ? textoRestantes(chat.restantesHora) : undefined}
            />
          }
        >
          {abriendo && vacio ? (
            <EsqueletoHilo />
          ) : vacio ? (
            <ChatIaBienvenida
              titulo="Pregúntame sobre los reportes"
              descripcion="Contesto con diagnóstico y acciones, no solo números."
              sugerencias={sugeridas}
              recientes={historial.conversaciones.slice(0, 3)}
              deshabilitado={chat.enviando || bloqueado}
              onPreguntar={(s) => enviar(s)}
              onRepetir={(c) => void repetirConversacion(c)}
            />
          ) : (
            <ChatIaMensajes
              mensajes={chat.mensajes}
              deshabilitado={chat.enviando || bloqueado}
              escribiendo={chat.enviando}
              textoEscribiendo="Analizando"
              onRepetir={repetir}
              onEditar={editar}
              renderRespuesta={(m) => <Respuesta texto={m.texto} />}
              renderPie={(m) => (
                <div className="w-full space-y-1">
                  {m.herramientas.length > 0 && (
                    <p className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Info className="size-3.5 shrink-0" />
                      <span className="min-w-0 truncate">Calculado con: {m.herramientas.map((h) => h.nombre).join(", ")}</span>
                    </p>
                  )}
                  <AccionesMensaje className="-ml-2">
                    <BotonCopiar texto={textoParaCopiarAnalista(m.texto)} etiqueta="Copiar plan" />
                  </AccionesMensaje>
                </div>
              )}
            />
          )}

          {chat.error && (
            <ChatIaAviso
              tono={chat.error.codigo === "limite_alcanzado" ? "aviso" : "error"}
              mensaje={chat.error.mensaje}
              onReintentar={bloqueado || chat.error.codigo === "limite_alcanzado" ? undefined : chat.reintentar}
              reintentando={chat.enviando}
            />
          )}

          <div ref={finRef} />
        </ChatIaCuerpo>
      </ChatIaPanel>
    </>
  );
}
